import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import { api } from '../config/api';
import CancellationModal from '../components/CancellationModal';
import EmailOTPVerificationPopup from '../components/EmailOTPVerificationPopup';
import './UserProfile.css';
import { BookingDetailsModal } from '../components/UserProfileAction';


const UserProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearUser, userDetails, setUserDetails, clearBooking, loading: authLoading } = useBooking();
  const [activeTab, setActiveTab] = useState('personal-details');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMenuOnly, setShowMenuOnly] = useState(isMobile);
  const [fullUserData, setFullUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
  
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowMenuOnly(true);   // mobile → menu first
    } else {
      setShowMenuOnly(false); // desktop → always show content
    }
  }, [isMobile]);





  // Get real user data from context or fetch from API
  useEffect(() => {
    const fetchUserData = async () => {
      // Wait until BookingContext has finished its async auth check.
      // On hard refresh, userDetails can briefly be null while the
      // context hydrates from localStorage / validates with the API.
      // If we redirected immediately, the page would jump to '/' before
      // the user could be restored.
      if (authLoading) {
        return;
      }

      if (userDetails && userDetails.userId) {
        try {
          setLoading(true);
          const userData = await api.getUserById(userDetails.userId);
          setFullUserData(userData);
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Fallback to userDetails from context
          setFullUserData({
            name: userDetails.name || '',
            email: userDetails.email || '',
            phone: userDetails.phone || userDetails.mobile || '',
            mobile: userDetails.mobile || userDetails.phone || '',
          });
        } finally {
          setLoading(false);
        }
      } else if (userDetails && (userDetails.email || userDetails.phone)) {
        // Guest user (has email/phone but no userId)
        setFullUserData({
          name: userDetails.name || '',
          email: userDetails.email || '',
          phone: userDetails.phone || userDetails.mobile || '',
          mobile: userDetails.mobile || userDetails.phone || '',
        });
        setLoading(false);
      } else {
        // Auth check finished and there is still no user — redirect to home.
        navigate('/');
      }
    };

    fetchUserData();
  }, [userDetails, navigate, authLoading]);

  // Merge fetched user data with anything we already have in context/localStorage
  const user = {
    name: fullUserData?.name || userDetails?.name || '',
    email: fullUserData?.email || userDetails?.email || '',
    phone: fullUserData?.phone || fullUserData?.mobile || userDetails?.phone || userDetails?.mobile || '',
    mobile: fullUserData?.mobile || fullUserData?.phone || userDetails?.mobile || userDetails?.phone || '',
    gender: fullUserData?.gender || userDetails?.gender || '',
    dateOfBirth: fullUserData?.dateOfBirth || fullUserData?.dob || userDetails?.dateOfBirth || userDetails?.dob || '',
    avatar: '/assets/img/user-avatar.png',
    // Google login fields
    authProvider: fullUserData?.authProvider || userDetails?.authProvider || (userDetails?.googleSignIn ? 'google' : 'phone'),
    profilePictureUrl: fullUserData?.profilePictureUrl || userDetails?.profilePictureUrl || '',
    googleId: fullUserData?.googleId || userDetails?.googleId || null,
  };

  const navigationItems = [
    { id: 'personal-details', label: 'Personal Details', icon: 'fa-user' },
    { id: 'my-bookings', label: 'My Bookings', icon: 'fa-ticket' },
    // { id: 'movie-alerts', label: 'Movie Alerts', icon: 'fa-bell' },//
    // { id: 'preferences', label: 'Preferences', icon: 'fa-film' },//
    // { id: 'alerts', label: 'Alerts', icon: 'fa-bullhorn' }
  ];


  const handleDeleteAccount = async () => {
    try {
      setLoading(true);

      await api.deleteUserAccount(userDetails?.userId);


      // Clear app state
      clearBooking();
      clearUser();

      // Close popup
      setShowDeletePopup(false);

      // Redirect
      navigate('/', { replace: true });
    } catch (error) {
      setShowDeletePopup(false);
      showError(error.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = () => {
    // Clear user details (will also clear from localStorage)
    clearUser();
    // Navigate to home page
    navigate('/');
  };

  // Handle updating user profile
  const handleUpdateUser = async (updatedFields) => {
    const userId = userDetails?.userId || userDetails?.id;
    if (!userId) {
      // Guest user - only save locally
      setFullUserData((prev) => ({ ...prev, ...updatedFields }));
      setUserDetails((prev) => ({ ...(prev || {}), ...updatedFields }));
      return { success: true, data: { ...user, ...updatedFields } };
    }

    try {
      // Normalize payload to what backend expects
      const payload = {
        name: updatedFields.name,
        email: updatedFields.email,
        phone: updatedFields.phone,
        gender: updatedFields.gender,
        dateOfBirth: updatedFields.dateOfBirth,
      };

      // Call the API
      const updatedUser = await api.updateUserProfile(userId, payload);

      // Update local state with server response
      setFullUserData((prev) => ({ ...prev, ...updatedUser }));

      // Update context while preserving userId/id/verified to avoid logout
      setUserDetails((prev) => ({
        ...prev, // 👈 CRITICAL: keeps userId, verified, role, etc.
        name: updatedUser.name || payload.name || prev?.name,
        email: updatedUser.email || payload.email || prev?.email,
        phone: updatedUser.phone || payload.phone || prev?.phone,
        mobile: updatedUser.phone || payload.phone || prev?.mobile,
        gender: updatedUser.gender || payload.gender || prev?.gender,
        dateOfBirth: updatedUser.dateOfBirth || payload.dateOfBirth || prev?.dateOfBirth,
        dob: updatedUser.dateOfBirth || payload.dateOfBirth || prev?.dob,
      }));




      return { success: true, data: updatedUser };
    } catch (error) {
      console.error('Failed to update user profile:', error);
      return { success: false, error: error.message || 'Failed to update profile' };
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'personal-details':
        return <PersonalDetails user={user} onUpdate={handleUpdateUser} userId={userDetails?.userId || userDetails?.id} />;
      case 'my-bookings':
        return <MyBookings />;
      case 'movie-alerts':
        return <MovieAlerts />;
      case 'preferences':
        return <Preferences />;
      case 'alerts':
      // return <Alerts />;
      default:
        return <PersonalDetails user={user} onUpdate={handleUpdateUser} userId={userDetails?.userId || userDetails?.id} />;
    }
  };


  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="profile-main-content">
          <div className="container">
            <div style={{ padding: '50px', textAlign: 'center' }}>
              <p>Loading profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      {/* Main Content */}
      <div className="profile-main-content">
        <div className="container">
          <div className="content-grid">
            {/* Left Sidebar */}
            {(!isMobile || showMenuOnly) && (
              <div className="profile-sidebar">

                {/* User Profile Info */}
                <div className="sidebar-user-info">
                  <div className="user-avatar">
                    {user.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={user.name || "User"}
                        className="avatar-image"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="avatar-circle"
                      style={{
                        display: user.profilePictureUrl ? "none" : "flex",
                      }}
                    >
                      <i className="fas fa-user"></i>
                    </div>
                  </div>
                  <div className="user-info">
                    <h3 className="user-name">
                      {user.name || "User"}
                      {(user.authProvider === "google" ||
                        user.authProvider === "both") && (
                        <span
                          className="google-badge"
                          title="Signed in with Google"
                        >
                          <svg
                            viewBox="0 0 48 48"
                            width="14"
                            height="14"
                            aria-hidden="true"
                          >
                            <path
                              fill="#4285F4"
                              d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
                            />
                            <path
                              fill="#34A853"
                              d="M24 46c5.95 0 10.94-1.97 14.59-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.48 2.1-5.75 0-10.62-3.88-12.36-9.1H4.34v5.7C7.96 41.07 15.4 46 24 46z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M11.64 28.16c-.44-1.32-.69-2.73-.69-4.16s.25-2.84.69-4.16v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.86l7.3-5.7z"
                            />
                            <path
                              fill="#EA4335"
                              d="M24 10.75c3.23 0 6.13 1.11 8.42 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.14l7.3 5.7c1.74-5.22 6.61-9.1 12.36-9.1z"
                            />
                          </svg>
                          Google
                        </span>
                      )}
                    </h3>
                    <p className="user-email">{user.email || "No email"}</p>
                    <p className="user-phone">
                      {user.phone || user.mobile || "No phone"}
                    </p>
                  </div>
                </div>

                {/* Separator */}
                <div className="sidebar-separator"></div>

                <nav className="profile-nav">
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab(item.id);
                        navigate(`/profile?tab=${item.id}`);
                      
                        if (isMobile) setShowMenuOnly(false);
                      }}

                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </button>
                  ))}
                  <button
                    className="nav-item delete-account"
                    onClick={() => setShowDeletePopup(true)}
                  >
                    <i className="fas fa-user-slash"></i>
                    <span>Delete Account</span>
                  </button>

                  <button
                    className="nav-item logout"
                    onClick={() => setShowLogoutPopup(true)}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>

                </nav>
              </div>
            )}

            {/* Right Content */}
            {(!isMobile || !showMenuOnly) && (
              <div className="profile-content">

                {/* MOBILE BACK BUTTON */}
                {isMobile && (
                  <button
                    className="mobile-back-btn"
                    onClick={() => setShowMenuOnly(true)}
                  >
                    ←
                  </button>
                )}

                {renderContent()}
              </div>
            )}

          </div>
        </div>
      </div>
      {showDeletePopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowDeletePopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-user-slash"></i>
            </div>

            <div className="error-popup-message">
              <strong>This action is irreversible.</strong>
              <br />
              Your account will be permanently deleted.
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center' }}>
              <button
                className="error-popup-button"
                onClick={() => setShowDeletePopup(false)}
              >
                Cancel
              </button>

              <button
                className="error-popup-button"
                style={{ backgroundColor: '#dc3545', color: '#fff' }}
                onClick={handleDeleteAccount}
                disabled={loading}
              >
                {loading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowLogoutPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-sign-out-alt"></i>
            </div>

            <div className="error-popup-message">
              <strong>Are you sure you want to logout?</strong>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginTop: '16px',
                justifyContent: 'center'
              }}
            >
              <button
                className="error-popup-button"
                onClick={() => setShowLogoutPopup(false)}
              >
                Cancel
              </button>

              <button
                className="error-popup-button"
                style={{ backgroundColor: '#6c757d', color: '#fff' }}
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">
              {errorMessage}
            </div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}


    </div>
  );
};

// Personal Details Component
const PersonalDetails = ({ user, onUpdate, userId }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || user.mobile || '',
    gender: user.gender || '',
    dateOfBirth: user.dateOfBirth || user.dob || '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEmailOTP, setShowEmailOTP] = useState(false);
  const [pendingProfileData, setPendingProfileData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || user.mobile || '',
      gender: user.gender || '',
      dateOfBirth: user.dateOfBirth || user.dob || '',
    });
  }, [user]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
  
    const listener = () => setIsMobile(media.matches);
    listener();
  
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleDateString();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!formData.name || !formData.email || !formData.phone) {
      setError('Name, email, and phone are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone (basic check - should be at least 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    // ✅ Normalize before comparing (IMPORTANT FIX)
    const normalize = (val) => (val || '').toString().trim();    

    // Check if any field actually changed
    // const hasChanges =
    //   formData.name !== user.name ||
    //   formData.email !== user.email ||
    //   formData.phone !== user.phone ||
    //   formData.gender !== user.gender ||
    //   formData.dateOfBirth !== user.dateOfBirth;
    const hasChanges =
      normalize(formData.name) !== normalize(user.name) ||
      normalize(formData.email) !== normalize(user.email) ||
      normalize(formData.phone) !== normalize(user.phone) ||
      normalize(formData.gender) !== normalize(user.gender) ||
      normalize(formData.dateOfBirth) !== normalize(user.dateOfBirth);

    if (!hasChanges) {
      setError('No changes detected');
      return;
    }

    // For logged-in users, require OTP verification
    if (userId) {
      setPendingProfileData(formData);
      setShowEmailOTP(true);
    } else {
      // Guest users - save directly
      await performProfileUpdate(formData);
    }
  };

  const performProfileUpdate = async (profileData) => {
    setSaving(true);
    try {
      const result = await onUpdate?.(profileData);
      if (result?.success) {
        setSuccess('Profile updated successfully');
        setEditMode(false);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result?.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleOTPVerified = async (verificationResult) => {
    if (verificationResult.verified) {
      // Proceed with profile update using the verified token
      await performProfileUpdate(pendingProfileData);
      setShowEmailOTP(false);
      setPendingProfileData(null);
    }
  };

  const handleCancel = () => {
    // Reset form data to current user data
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || user.mobile || '',
      gender: user.gender || '',
      dateOfBirth: user.dateOfBirth || user.dob || '',
    });
    setError('');
    setSuccess('');
    setEditMode(false);
  };

  // Format date for date input (YYYY-MM-DD)
  const formatDateForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const actionButtons = (
    <div className="details-actions">
      {editMode ? (
        <>
          <button className="secondary-btn" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button className="primary-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      ) : (
        <button className="edit-btn" onClick={() => setEditMode(true)}>
          Edit Details
        </button>
      )}
    </div>
  );  

  return (
    <>
      <div className="personal-details">
      <div className="content-header">
  <h3>Contact Information</h3>

  {!isMobile && actionButtons}
</div>

      {(error || success) && (
        <div
          style={{
            marginBottom: '20px',
            padding: '12px 16px',
            borderRadius: '6px',
            color: error ? '#b3261e' : '#0f5132',
            background: error ? '#f8d7da' : '#d1e7dd',
            border: `1px solid ${error ? '#f5c2c7' : '#badbcc'}`
          }}
        >
          {error || success}
        </div>
      )}
        <div className="details-grid">
          <div className="detail-item">
            <label>Full Name</label>
            {editMode ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <span>{user.name || '-'}</span>
            )}
          </div>
          <div className="detail-item">
            <label>Email</label>
            {editMode ? (
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <span>{user.email || '-'}</span>
            )}
          </div>
          <div className="detail-item">
            <label>Phone</label>
            {editMode ? (
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <span>{user.phone || user.mobile || '-'}</span>
            )}
          </div>
          <div className="detail-item">
            <label>Gender</label>
            {editMode ? (
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            ) : (
              <span>{user.gender || '-'}</span>
            )}
          </div>
          <div className="detail-item">
            <label>Date of Birth</label>
            {editMode ? (
              <input
                type="date"
                name="dateOfBirth"
                value={formatDateForInput(formData.dateOfBirth)}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <span>{formatDate(user.dateOfBirth || user.dob)}</span>
            )}
          </div>
        </div>
        {isMobile && actionButtons}
      </div>

      {/* Email OTP Verification Popup */}
      <EmailOTPVerificationPopup
        isOpen={showEmailOTP}
        onClose={() => {
          setShowEmailOTP(false);
          setPendingProfileData(null);
        }}
        onOTPVerified={handleOTPVerified}
        email={user.email}
        userId={userId}
        pendingProfileData={pendingProfileData}
      />
    </>
  );
};

// My Bookings Component
const MyBookings = () => {
  const navigate = useNavigate(); // 👈 ADD THIS
  const { userDetails } = useBooking();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moviePosters, setMoviePosters] = useState({}); // Store movie title -> poster mapping
  const [cancellationModal, setCancellationModal] = useState(null);
  const [cancellationDeadline, setCancellationDeadline] = useState(2); // hours
  const [refundProcessingDays, setRefundProcessingDays] = useState(7); // days
  const [selectedBookingForDetails, setSelectedBookingForDetails] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isGuest, setIsGuest] = useState(false);
  const [guestLookupForm, setGuestLookupForm] = useState({
    email: '',
    phone: '',
  });
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  useEffect(() => {
    const fetchBookings = async () => {
      // Check if user is logged in (has userId)
      if (userDetails && userDetails.userId) {
        try {
          setLoading(true);
          setError(null);
          setIsGuest(false);
          const result = await api.getUserBookings(userDetails.userId, currentPage, 10);
          const userBookings = result.bookings || [];
          setBookings(userBookings);
          setPagination(result.pagination || pagination);

          // Fetch settings for cancellation deadline
          try {
            const settings = await api.getSettings();
            if (settings && settings.booking) {
              setCancellationDeadline(settings.booking.cancellationDeadline || 4);
              setRefundProcessingDays(settings.booking.refundProcessingDays || 7);
            }
          } catch (settingsError) {
            console.warn('Could not fetch settings, using defaults', settingsError);
          }

          // Fetch movie posters for all unique movie titles
          if (userBookings && userBookings.length > 0) {
            const uniqueMovieTitles = [...new Set(userBookings.map(b => b.movieTitle).filter(Boolean))];
            const posterMap = {};

            // Try to fetch movie details for each title
            for (const title of uniqueMovieTitles) {
              try {
                // Search for movie by title
                const movies = await api.getMovies({ search: title, limit: 10 });
                if (movies && movies.length > 0) {
                  // Find exact or partial match
                  const movie = movies.find(m => {
                    const mTitle = (m.title || '').toLowerCase();
                    const bTitle = (title || '').toLowerCase();
                    return mTitle === bTitle ||
                      mTitle.includes(bTitle) ||
                      bTitle.includes(mTitle);
                  }) || movies[0];

                  // Get poster URL (check multiple possible field names)
                  const posterUrl = movie.posterUrl || movie.poster || movie.image || movie.bannerUrl || movie.banner;
                  if (posterUrl) {
                    posterMap[title] = posterUrl;
                  }
                }
              } catch (err) {
                console.warn(`Could not fetch poster for movie: ${title}`, err);
                // Fallback: try to use a default mapping from common movie titles
                const defaultPosters = {
                  'EMPURAAN': '/assets/img/film-banner-booking.png',
                  'THE PET DETECTIVE': '/assets/img/film-banner-booking.png',
                  'LEO': '/assets/img/leo-movie.jpeg',
                  'JAWAN': '/assets/img/movie-2.png',
                  'PATHAAN': '/assets/img/movie-3.png',
                };
                const normalizedTitle = title.toUpperCase();
                if (defaultPosters[normalizedTitle]) {
                  posterMap[title] = defaultPosters[normalizedTitle];
                }
              }
            }

            setMoviePosters(posterMap);
          }
        } catch (err) {
          console.error('Error fetching bookings:', err);
          setError('Failed to load bookings');
          setBookings([]);
        } finally {
          setLoading(false);
        }
      } else if (userDetails && userDetails.email && userDetails.phone) {
        // Guest user - fetch guest bookings
        try {
          setLoading(true);
          setError(null);
          setIsGuest(true);
          const cleanPhone = userDetails.phone.replace(/\s+/g, '').replace(/-/g, '');
          const result = await api.getGuestBookings(userDetails.email, cleanPhone, currentPage, 10);
          const guestBookings = result.bookings || [];
          setBookings(guestBookings);
          setPagination(result.pagination || pagination);

          // Fetch settings for cancellation deadline
          try {
            const settings = await api.getSettings();
            if (settings && settings.booking) {
              setCancellationDeadline(settings.booking.cancellationDeadline || 2);
              setRefundProcessingDays(settings.booking.refundProcessingDays || 7);
            }
          } catch (settingsError) {
            console.warn('Could not fetch settings, using defaults', settingsError);
          }

          // Fetch movie posters for all unique movie titles
          if (guestBookings && guestBookings.length > 0) {
            const uniqueMovieTitles = [...new Set(guestBookings.map(b => b.movieTitle).filter(Boolean))];
            const posterMap = {};

            // Try to fetch movie details for each title
            for (const title of uniqueMovieTitles) {
              try {
                // Search for movie by title
                const movies = await api.getMovies({ search: title, limit: 10 });
                if (movies && movies.length > 0) {
                  // Find exact or partial match
                  const movie = movies.find(m => {
                    const mTitle = (m.title || '').toLowerCase();
                    const bTitle = (title || '').toLowerCase();
                    return mTitle === bTitle ||
                      mTitle.includes(bTitle) ||
                      bTitle.includes(mTitle);
                  }) || movies[0];

                  // Get poster URL (check multiple possible field names)
                  const posterUrl = movie.posterUrl || movie.poster || movie.image || movie.bannerUrl || movie.banner;
                  if (posterUrl) {
                    posterMap[title] = posterUrl;
                  }
                }
              } catch (err) {
                console.warn(`Could not fetch poster for movie: ${title}`, err);
                // Fallback: try to use a default mapping from common movie titles
                const defaultPosters = {
                  'EMPURAAN': '/assets/img/film-banner-booking.png',
                  'THE PET DETECTIVE': '/assets/img/film-banner-booking.png',
                  'LEO': '/assets/img/leo-movie.jpeg',
                  'JAWAN': '/assets/img/movie-2.png',
                  'PATHAAN': '/assets/img/movie-3.png',
                };
                const normalizedTitle = title.toUpperCase();
                if (defaultPosters[normalizedTitle]) {
                  posterMap[title] = defaultPosters[normalizedTitle];
                }
              }
            }

            setMoviePosters(posterMap);
          }
        } catch (err) {
          console.error('Error fetching guest bookings:', err);
          setError('Failed to load bookings');
          setBookings([]);
        } finally {
          setLoading(false);
        }
      } else {
        // No user details - show guest lookup form
        setIsGuest(true);
        setShowGuestForm(true);
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userDetails, currentPage]);

  const handleGuestLookup = async () => {
    if (!guestLookupForm.email || !guestLookupForm.phone) {
      showError('Please enter both email and phone number');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestLookupForm.email)) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const cleanPhone = guestLookupForm.phone.replace(/\s+/g, '').replace(/-/g, '');
      const result = await api.getGuestBookings(guestLookupForm.email.trim().toLowerCase(), cleanPhone, 1, 10);
      const guestBookings = result.bookings || [];
      setBookings(guestBookings);
      setPagination(result.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      });
      setCurrentPage(1);
      setShowGuestForm(false);

      // Fetch movie posters (same logic as above)
      if (guestBookings && guestBookings.length > 0) {
        const uniqueMovieTitles = [...new Set(guestBookings.map(b => b.movieTitle).filter(Boolean))];
        const posterMap = {};

        for (const title of uniqueMovieTitles) {
          try {
            const movies = await api.getMovies({ search: title, limit: 10 });
            if (movies && movies.length > 0) {
              const movie = movies.find(m => {
                const mTitle = (m.title || '').toLowerCase();
                const bTitle = (title || '').toLowerCase();
                return mTitle === bTitle || mTitle.includes(bTitle) || bTitle.includes(mTitle);
              }) || movies[0];

              const posterUrl = movie.posterUrl || movie.poster || movie.image || movie.bannerUrl || movie.banner;
              if (posterUrl) {
                posterMap[title] = posterUrl;
              }
            }
          } catch (err) {
            console.warn(`Could not fetch poster for movie: ${title}`, err);
          }
        }

        setMoviePosters(posterMap);
      }
    } catch (err) {
      console.error('Error fetching guest bookings:', err);
      setError(err.message || 'Failed to load bookings. Please check your email and phone number.');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (booking) => {
    const status = (booking?.uiStatus || booking?.status || 'pending').toLowerCase();
    switch (status) {
      case 'completed':
        return <span className="status-badge completed">Completed</span>;
      case 'cancelled':
        return <span className="status-badge cancelled">Cancelled</span>;
      case 'upcoming':
        return <span className="status-badge upcoming">Upcoming</span>;
      default:
        return <span className="status-badge pending">Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // const formatDateTime = (dateTimeString) => {
  //   if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
  //   try {
  //     const date = new Date(dateTimeString);
  //     return {
  //       date: date.toLocaleDateString('en-US', {
  //         weekday: 'short',
  //         month: 'short',
  //         day: 'numeric',
  //         year: 'numeric'
  //       }),
  //       time: date.toLocaleTimeString('en-US', {
  //         hour: '2-digit',
  //         minute: '2-digit',
  //         hour12: true
  //       })
  //     };
  //   } catch (e) {
  //     return { date: dateTimeString, time: 'N/A' };
  //   }
  // };  //commenetd out for utc time fix

  const formatBookingDate = (dateString) => {
    if (!dateString) return 'N/A';
  
    try {
      const date = new Date(dateString);
  
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
    } catch {
      return dateString;
    }
  };
  
  const formatBookingTime = (timeString) => {
    if (!timeString) return 'N/A';
  
    try {
      const [hours, minutes] = timeString.split(':');
  
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
  
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // const calculateRefundAmount = (booking) => {
  //   const total = parseFloat(booking.totalAmount || 0);
  //   const convenienceFee = parseFloat(booking.convenienceFee || 0);
  
  //   const ticketAmount = total - convenienceFee;
  //   const deduction = ticketAmount * 0.20;
  
  //   const refund = total - (convenienceFee + deduction);
  
  //   return Math.max(refund, 0).toFixed(2);
  // };

  const canCancelBooking = (booking) => {
    if (!booking) {
      // console.log('canCancelBooking: No booking provided');
      return false;
    }

    // Check if booking is already cancelled
    const status = (booking.uiStatus || booking.status || '').toLowerCase();
    // console.log('canCancelBooking: Checking booking', {
    //   id: booking.id,
    //   status: status,
    //   uiStatus: booking.uiStatus,
    //   bookingStatus: booking.status,
    //   showDateTime: booking.showDateTime,
    //   cancellationDeadline,
    // });

    if (status === 'cancelled') {
      // console.log('canCancelBooking: Already cancelled');
      return false;
    }

    // Allow cancellation for:
    // - 'upcoming' bookings (future shows)
    // - 'completed' bookings (confirmed/booked)
    // - 'confirmed' bookings (direct status)
    // - 'pending' bookings with completed payment
    const eligibleStatuses = ['upcoming', 'completed', 'confirmed'];
    if (!eligibleStatuses.includes(status) && status !== 'pending') {
      // console.log('canCancelBooking: Status not eligible for cancellation', status);
      return false;
    }

    // For pending bookings, only allow if payment is completed
    if (status === 'pending') {
      const paymentStatus = (booking.paymentStatus || '').toLowerCase();
      if (paymentStatus !== 'completed' && paymentStatus !== 'paid') {
        // console.log('canCancelBooking: Pending booking but payment not completed', paymentStatus);
        return false;
      }
    }

    if (!booking.showDateTime) {
      // console.log('canCancelBooking: No showDateTime');
      return false;
    }

    const showDateTime = new Date(booking.showDateTime);
    const now = new Date();
    const hoursUntilShow = (showDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    // console.log('canCancelBooking: Hours until show', hoursUntilShow, 'Deadline:', cancellationDeadline);

    // Must be at least cancellationDeadline hours before show
    const canCancel = hoursUntilShow >= cancellationDeadline;
    // console.log('canCancelBooking: Result', canCancel);
    return canCancel;
  };

  const handleCancelClick = (booking) => {
    // console.log('ALL BOOKING FIELDS:', JSON.stringify(booking));
    if (!canCancelBooking(booking)) {
      const showDateTime = new Date(booking.showDateTime);
      const now = new Date();
      const hoursUntilShow = (showDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  
      showError(`Cancellation not allowed. Bookings can only be cancelled at least ${cancellationDeadline} hour(s) before the show. You have ${Math.round(hoursUntilShow * 10) / 10} hours remaining.`);
      return;
    }
  
    const paymentStatus = (booking.paymentStatus || '')
  .toString()
  .trim()
  .toLowerCase();

const isRefund = ['completed', 'paid', 'success'].includes(paymentStatus);

// console.log('Refund Decision:', {
//   paymentStatus,
//   isRefund
// });
    setCancellationModal({
      ...booking,
      isRefund,
    });
  };

  

  const handleCancelConfirm = async () => {
    if (!cancellationModal) return;  // ← FIRST line
  
    // console.log('Final Action:', cancellationModal.isRefund);
  
    const userId = userDetails?.userId || null;
    const bookingId = cancellationModal.id; // ← use .id consistently (your booking object uses 'id')
  
    try {
      let result;
  
      if (cancellationModal.isRefund) {
        result = await api.refundBooking(
          bookingId,  // ← was cancellationModal.bookingId (undefined!)
          {
            cancellationReason: 'Customer cancelled',
            cancelledBy: 'customer' // ← backend only needs this
          }
        );
      } else {
        result = await api.cancelBooking(bookingId, userId);
      }
  
      if (result.success) {
        showError(result.data?.message || 'Booking cancelled successfully');
  
        const refreshResult = await api.getUserBookings(userDetails.userId, currentPage, 10);
        setBookings(refreshResult.bookings || []);
        setPagination(refreshResult.pagination || pagination);
        setCancellationModal(null);
      } else {
        throw new Error(result.error || 'Failed');
      }
  
    } catch (error) {
      console.error('Cancellation error:', error);
      showError(error.message || 'Failed to process request');
    }
  };
  if (loading) {
    return (
      <div className="my-bookings">
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <p>Loading bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-bookings">
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <p style={{ color: 'red' }}>{error}</p>
        </div>
      </div>
    );
  }

  // Show guest lookup form if no user details
  if (showGuestForm && !userDetails) {
    return (
      <div className="my-bookings">
        <div className="bookings-header">
          <h3>View Guest Bookings</h3>
          <p style={{ color: '#6c757d', marginTop: '10px' }}>Enter your email and phone number to view your bookings</p>
        </div>

        <div className="guest-lookup-form" style={{
          maxWidth: '500px',
          margin: '40px auto',
          padding: '30px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Email <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="email"
              value={guestLookupForm.email}
              onChange={(e) => setGuestLookupForm({ ...guestLookupForm, email: e.target.value })}
              placeholder="your.email@example.com"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Phone Number <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="tel"
              value={guestLookupForm.phone}
              onChange={(e) => setGuestLookupForm({ ...guestLookupForm, phone: e.target.value })}
              placeholder="+91 9876543210"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            onClick={handleGuestLookup}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: '#2f3192',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Loading...' : 'View Bookings'}
          </button>

          {error && (
            <div style={{
              marginTop: '15px',
              padding: '10px',
              background: '#fff5f5',
              border: '1px solid #feb2b2',
              borderRadius: '4px',
              color: '#c53030'
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-bookings">
      <div className="bookings-header">
        <h3>{isGuest ? 'Guest Bookings' : 'My Bookings'}</h3>
        {isGuest && userDetails && (
          <p style={{ color: '#6c757d', marginTop: '5px', fontSize: '14px' }}>
            Viewing bookings for {userDetails.email}
          </p>
        )}
        <div className="bookings-stats">
          <div className="stat-item">
            <span className="stat-number">{bookings.length}</span>
            <span className="stat-label">Total Bookings</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {bookings.filter(b =>
                b.paymentStatus === 'completed' &&
                b.status !== 'cancelled'
              ).length}
            </span>
            <span className="stat-label">Confirmed</span>
          </div>
        </div>
      </div>

      <div className="bookings-list">
        {bookings.map((booking) => {
          const dateTime = {
            date: formatBookingDate(booking.showDate),
            time: formatBookingTime(booking.showTime)
          };
          const posterUrl = moviePosters[booking.movieTitle] || booking.poster || '/assets/img/default-movie-poster.png';
          const seatList = booking.seatsLabel || (Array.isArray(booking.seats) ? booking.seats.join(', ') : '');
          const displayStatus = (booking.uiStatus || booking.status || 'pending').toLowerCase();

          // Debug logging
          const canCancel = canCancelBooking(booking);
          // console.log('Booking render:', {
          //   id: booking.id,
          //   status: booking.status,
          //   uiStatus: booking.uiStatus,
          //   displayStatus,
          //   paymentStatus: booking.paymentStatus,
          //   showDateTime: booking.showDateTime,
          //   canCancel,
          // });

          return (
            <div key={booking.id} className="booking-card">
              <div className="booking-poster">
                {posterUrl && posterUrl !== '/assets/img/default-movie-poster.png' ? (
                  <img
                    src={posterUrl}
                    alt={booking.movieTitle || 'Movie'}
                    onError={(e) => {
                      // Fallback to default if image fails to load
                      e.target.src = '/assets/img/default-movie-poster.png';
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#666',
                    flexDirection: 'column',
                    padding: '10px'
                  }}>
                    <i className="fas fa-film" style={{ fontSize: '24px', marginBottom: '5px' }}></i>
                    <span>{booking.movieTitle || 'Movie'}</span>
                  </div>
                )}
              </div>

              <div className="booking-details">
                <div className="booking-header">
                  <h4 className="movie-title">{booking.movieTitle || 'Movie'}</h4>
                  {getStatusBadge(booking)}
                </div>

                <div className="booking-info">
                  <div className="info-row">
                    <i className="fas fa-building"></i>
                    <span>{booking.cinemaName || 'Cinema'}</span>
                  </div>
                  {booking.cinemaLocation && (
                    <div className="info-row">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{booking.cinemaLocation}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <i className="fas fa-calendar"></i>
                    <span>{dateTime.date}</span>
                  </div>
                  <div className="info-row">
                    <i className="fas fa-clock"></i>
                    <span>{dateTime.time}</span>
                  </div>
                  {booking.screenName && (
                    <div className="info-row">
                      <i className="fas fa-tv"></i>
                      <span>{booking.screenName}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <i className="fas fa-chair"></i>
                    <span>
                      Seats: {booking.seatCount || 0}
                      {seatList && <span className="seat-label"> ({seatList})</span>}
                    </span>
                  </div>
                  <div className="info-row">
                    <i className="fas fa-ticket"></i>
                    <span>Booking ID: {booking.bookingReference || booking.bookingNumber || booking.id || 'NA'}</span>
                  </div>
                  <div className="info-row">
                    <i className="fas fa-rupee-sign"></i>
                    <span>
                      ₹{parseFloat(booking.totalAmount || booking.ticketPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="info-row">
                    <i className="fas fa-info-circle"></i>
                    <span>Payment: {(booking.paymentStatus || 'pending').toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <div className="booking-actions">
                {/* Show cancel button if booking is eligible for cancellation */}
                {canCancel && (
                  <button
                    className="action-btn cancel-btn"
                    onClick={() => handleCancelClick(booking)}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                )}

                {/* Show other action buttons for confirmed/completed bookings */}
                {(displayStatus === 'completed' || displayStatus === 'confirmed' || displayStatus === 'upcoming') && (
                  <>
                    {/* <button className="action-btn download-btn">
                      <i className="fas fa-download"></i>
                      Download
                    </button>
                    <button className="action-btn share-btn">
                      <i className="fas fa-share"></i>
                      Share
                    </button> */}
                  </>
                )}

                <button onClick={() => setSelectedBookingForDetails(booking)} className="action-btn details-btn">
                  <i className="fas fa-eye"></i>
                  Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {bookings.length === 0 && !loading && (
        <div className="empty-bookings">
          <div className="empty-icon">
            <i className="fas fa-ticket-alt"></i>
          </div>
          <h4>No Bookings Yet</h4>
          <p>Your booking history will appear here once you make your first booking.</p>
          <button className="secondary-btn" onClick={() => navigate("/")}>Browse Movies</button>
        </div>
      )}

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="bookings-pagination" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '30px',
          padding: '20px'
        }}>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={!pagination.hasPreviousPage || loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: pagination.hasPreviousPage ? '#fff' : '#f5f5f5',
              cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed',
              opacity: pagination.hasPreviousPage ? 1 : 0.5
            }}
          >
            <i className="fas fa-chevron-left"></i> Previous
          </button>

          <span style={{ padding: '0 10px' }}>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
            disabled={!pagination.hasNextPage || loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: pagination.hasNextPage ? '#fff' : '#f5f5f5',
              cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
              opacity: pagination.hasNextPage ? 1 : 0.5
            }}
          >
            Next <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {cancellationModal && (
        <CancellationModal
          booking={cancellationModal}
          onClose={() => setCancellationModal(null)}
          onConfirm={handleCancelConfirm}
          cancellationDeadline={cancellationDeadline}
          refundProcessingDays={refundProcessingDays}
        />
      )}
      {selectedBookingForDetails && (
        <BookingDetailsModal
          booking={selectedBookingForDetails}
          onClose={() => setSelectedBookingForDetails(null)}
        />
      )}

      {showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">
              {errorMessage}
            </div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

// Movie Alerts Component
const MovieAlerts = () => {
  return (
    <div className="movie-alerts">
      <h3>Movie Alerts</h3>
      <p>Set up alerts for your favorite movies.</p>
    </div>
  );
};

// Preferences Component
const Preferences = () => {
  return (
    <div className="preferences">
      <h3>Preferences</h3>
      <p>Manage your movie preferences.</p>
    </div>
  );
};

// Alerts Component
// const Alerts = () => {
//   const [alerts, setAlerts] = useState({
//     transactional: {
//       whatsapp: false
//     },
//     promotional: {
//       enabled: true,
//       sms: true,
//       whatsapp: true,
//       email: true,
//       push: true
//     }
//   });

//   const handleToggle = (section, field) => {
//     setAlerts(prev => ({
//       ...prev,
//       [section]: {
//         ...prev[section],
//         [field]: !prev[section][field]
//       }
//     }));
//   };

//   const handleSubmit = () => {
//     console.log('Alerts updated:', alerts);
//     // Handle form submission
//   };

//   return (
//     <div className="alerts">
//       <h3>Alerts</h3>

//       {/* Transactional Alerts */}
//       <div className="alert-section">
//         <div className="section-header">
//           <div className="section-icon">
//             <i className="fas fa-ticket-alt"></i>
//           </div>
//           <div className="section-title">
//             <h4>Transactional Alerts</h4>
//             <p className="section-description">Enable this to get your tickets information</p>
//           </div>
//         </div>

//         <div className="alert-options">
//           <div className="alert-option">
//             <div className="option-content">
//               <div className="option-icon">
//                 <i className="fab fa-whatsapp"></i>
//               </div>
//               <span className="option-label">WhatsApp</span>
//             </div>
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="transactional-whatsapp"
//                 checked={alerts.transactional.whatsapp}
//                 onChange={() => handleToggle('transactional', 'whatsapp')}
//               />
//               <label htmlFor="transactional-whatsapp" className="toggle-slider"></label>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Promotional Alerts */}
//       <div className="alert-section">
//         <div className="section-header">
//           <div className="section-icon">
//             <i className="fas fa-bullhorn"></i>
//           </div>
//           <div className="section-title">
//             <h4>Promotional Alerts</h4>
//             <p className="section-description">Please select where all would you like to receive promotional alerts</p>
//           </div>
//           <div className="master-toggle">
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="promotional-enabled"
//                 checked={alerts.promotional.enabled}
//                 onChange={() => handleToggle('promotional', 'enabled')}
//               />
//               <label htmlFor="promotional-enabled" className="toggle-slider"></label>
//             </div>
//           </div>
//         </div>

//         <div className="alert-options">

//           <div className={`alert-option ${!alerts.promotional.enabled ? 'disabled' : ''}`}>
//             <div className="option-content">
//               <div className="option-icon">
//                 <i className="fas fa-sms"></i>
//               </div>
//               <span className="option-label">SMS Notifications</span>
//             </div>
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="promotional-sms"
//                 checked={alerts.promotional.sms}
//                 disabled={!alerts.promotional.enabled}
//                 onChange={() => handleToggle('promotional', 'sms')}
//               />
//               <label htmlFor="promotional-sms" className="toggle-slider"></label>
//             </div>
//           </div>

//           <div className={`alert-option ${!alerts.promotional.enabled ? 'disabled' : ''}`}>
//             <div className="option-content">
//               <div className="option-icon">
//                 <i className="fab fa-whatsapp"></i>
//               </div>
//               <span className="option-label">WhatsApp</span>
//             </div>
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="promotional-whatsapp"
//                 checked={alerts.promotional.whatsapp}
//                 disabled={!alerts.promotional.enabled}
//                 onChange={() => handleToggle('promotional', 'whatsapp')}
//               />
//               <label htmlFor="promotional-whatsapp" className="toggle-slider"></label>
//             </div>
//           </div>

//           <div className={`alert-option ${!alerts.promotional.enabled ? 'disabled' : ''}`}>
//             <div className="option-content">
//               <div className="option-icon">
//                 <i className="fas fa-envelope"></i>
//               </div>
//               <span className="option-label">Email Alerts</span>
//             </div>
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="promotional-email"
//                 checked={alerts.promotional.email}
//                 disabled={!alerts.promotional.enabled}
//                 onChange={() => handleToggle('promotional', 'email')}
//               />
//               <label htmlFor="promotional-email" className="toggle-slider"></label>
//             </div>
//           </div>

//           <div className={`alert-option ${!alerts.promotional.enabled ? 'disabled' : ''}`}>
//             <div className="option-content">
//               <div className="option-icon">
//                 <i className="fas fa-bell"></i>
//               </div>
//               <span className="option-label">Push Notifications</span>
//             </div>
//             <div className="toggle-switch">
//               <input
//                 type="checkbox"
//                 id="promotional-push"
//                 checked={alerts.promotional.push}
//                 disabled={!alerts.promotional.enabled}
//                 onChange={() => handleToggle('promotional', 'push')}
//               />
//               <label htmlFor="promotional-push" className="toggle-slider"></label>
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="alerts-actions">
//         <button className="submit-btn" onClick={handleSubmit}>
//           Submit
//         </button>
//       </div>
//     </div>
//   );


// };

// Saved Cards Component
const SavedCards = () => {
  return (
    <div className="saved-cards">
      <h3>Saved Cards</h3>
      <p>Manage your saved payment methods.</p>
    </div>
  );
};

// Gift Card Balance Component
const GiftCardBalance = () => {
  return (
    <div className="gift-card-balance">
      <h3>Gift Card Balance</h3>
      <p>Check your gift card balance and history.</p>
    </div>
  );
};

export default UserProfile;
